import { createRef, useState } from 'react';

import { TextInput, useMedia } from '@opendoor/bricks-next';
import { showToast } from '@opendoor/bricks/complex/Toast';
import {
  ComboInputElement,
  ComboInputElementProps,
  ComboInputOption,
} from '@opendoor/cinderblocks/growth/novo/inputs/ComboInput';
import Field from '@opendoor/cinderblocks/growth/novo/inputs/Field';
import { Sentry } from '@opendoor/observability';

import { BottomBar } from 'src/components/flowPage/twoColumn/BottomBar';
import { getErrorMessage } from 'src/components/flowPage/twoColumn/ErrorMessages';
import FlowPageHeader from 'src/components/flowPage/twoColumn/FlowPageHeader';
import { ProgressIndicator } from 'src/components/flowPage/twoColumn/ProgressIndicator';
import NavigationLayout from 'src/components/layouts/NavigationLayout';
import { PageHeader, PageLayout } from 'src/components/layouts/PageLayout';
import {
  QuestionFieldContainer,
  QuestionFieldsContainer,
} from 'src/components/layouts/QuestionField';
import {
  Column,
  ColumnContainer,
  TwoColumnPageContent,
} from 'src/components/layouts/TwoColumnPageLayout';
import { ReceptionLoader } from 'src/components/ReceptionLoader';
import { US_STATE_CODES_OPTIONS } from 'src/helpers/markets';
import { showPrompt } from 'src/helpers/showPrompt';
import { api, useCreateSellerInputMutation } from 'src/store/api';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import { useTrackLeadQuery } from 'src/store/httpApi';
import { getChannelQueryParam, getInApp, getTrackingKeywords } from 'src/store/router/selectors';
import { cancelManualAddressVerification, UpdatedAddress } from 'src/store/sellerFlow/actions';
import {
  address as getAddress,
  channel as getChannel,
  leadToken as leadTokenSelector,
} from 'src/store/sellerFlow/selectors';
import { SellerInputAddress } from 'src/store/sellerInput';

import { navigateToCoreFlow } from './MissingUnitNumberPage';

const isAddressEmpty = (address: SellerInputAddress) => {
  return (
    !address.street1 &&
    !address.city &&
    !address.state &&
    !address.postalCode &&
    !address.unitNumber
  );
};

export const toUpdatedAddress = (siAddress: SellerInputAddress): UpdatedAddress => {
  // TODO: figure out how we can clean up the street vs street1 deal
  // ignore street, we only every want to save street1
  const street = siAddress.street1 ?? siAddress.street;
  return {
    city: siAddress.city,
    postalCode: siAddress.postalCode,
    state: siAddress.state,
    street: street,
    // this is the only field that is different between these types
    unit: siAddress.unitNumber,
  };
};

const AddressEntryQuestionConfig = [
  {
    key: 'street1',
    type: 'single_line',
    label: 'Street address',
    placeholder: 'Street address',
    required: true,
  },
  {
    key: 'unitNumber',
    type: 'single_line',
    label: 'Unit number',
    placeholder: 'e.g. 123',
  },
  {
    key: 'city',
    type: 'single_line',
    label: 'City',
    placeholder: 'City',
    required: true,
  },
  {
    key: 'state',
    type: 'combo_input',
    label: 'State',
    placeholder: 'State',
    required: true,
  },
  {
    key: 'postalCode',
    type: 'single_line',
    label: 'Zip code',
    placeholder: 'Zip code',
    required: true,
  },
];

export const ManualAddressEntryPage = () => {
  const titleRef = createRef<HTMLHeadingElement>();
  const initialAddress = useAppSelector(getAddress);
  const sellerInputUUID = useAppSelector(leadTokenSelector);
  const inApp = useAppSelector(getInApp);

  const channelFromState = useAppSelector(getChannel);
  const channelQueryParam = useAppSelector(getChannelQueryParam);
  const channel = channelFromState || channelQueryParam;

  const [address, setAddress] = useState(initialAddress);
  const [showValidation, setShowValidation] = useState(false);
  const [loadingCancel, setLoadingCancel] = useState(false);
  const [modifyAddress, { isLoading: isModifyLoading, error: modifyError }] =
    api.useModifyAddressMutation();
  const [createSellerInput, { data: sellerInputData, error: sellerInputError }] =
    useCreateSellerInputMutation();
  const dispatch = useAppDispatch();
  const { smallerThanMD } = useMedia();

  if (modifyError || sellerInputError) {
    Sentry.withScope((scope) => {
      scope.setExtras({
        error: modifyError || sellerInputError,
        address,
        sellerInputUUID,
      });
      Sentry.captureMessage('ManualAddressEntry: GraphQL error');
    });
    showToast(
      'error',
      'Oops, something went wrong! Please try again.',
      'reception-fe-manual-address-entry-gql-error-toast',
      {
        toastOptions: { toastId: 'error-toast', autoClose: 7000 },
      },
    );
  }

  const trackingKeywords = useAppSelector(getTrackingKeywords);
  // only track when we have keywords and created a seller input
  const doTracking = trackingKeywords && sellerInputData;
  const { isLoading: trackLoading } = useTrackLeadQuery(trackingKeywords, {
    skip: !doTracking,
  });

  // if we are not tracking or we are done with tracking and we have modified or
  // created the seller input then send the user to the start of the flow
  if ((!trackingKeywords || !trackLoading) && sellerInputData) {
    const uuid =
      sellerInputData?.reception.createSellerInputWithTrackingDataFromCookies?.sellerInput?.uuid;
    if (uuid) {
      navigateToCoreFlow(sellerInputUUID, inApp);
    } else {
      Sentry.withScope((scope) => {
        scope.setExtras({
          response: sellerInputData,
          sellerInputUUID: sellerInputUUID,
          address,
          channel,
        });
        Sentry.captureMessage('ManualAddressEntry: CreateSellerInput response had no uuid');
      });
    }
  }

  const nextButtonOnClickHandler = () => {
    setShowValidation(true);

    // If any required questions are empty, scroll 1st errored input into view
    if (
      AddressEntryQuestionConfig.find(
        (question) => question.required && address[question.key].trim() == '',
      )
    ) {
      const el = document.querySelector<HTMLLabelElement>('label[data-has-error="true"]');
      if (el) {
        el.focus();
        el.scrollIntoView();
      }
    } else {
      if (!sellerInputUUID) {
        // we call navigateToCoreFlow above when useTrackLeadQuery completes
        createSellerInput({ address: toUpdatedAddress(address), channel });
      } else {
        modifyAddress({
          sellerInputUUID,
          address: toUpdatedAddress(address),
        }).then(() => {
          navigateToCoreFlow(sellerInputUUID, inApp);
        });
      }
    }
  };

  return (
    <PageLayout>
      <PageHeader />
      <TwoColumnPageContent>
        <ColumnContainer>
          <Column>
            {isAddressEmpty(initialAddress) ? (
              <FlowPageHeader
                title="Enter your full address"
                subtitle="Let's make sure we've got the right home before we get started."
                ref={titleRef}
              />
            ) : (
              <FlowPageHeader
                title="Is this the correct address?"
                subtitle="Confirm this is your home or enter a new address."
                ref={titleRef}
              />
            )}
          </Column>
          <Column>
            <form>
              <QuestionFieldsContainer>
                {AddressEntryQuestionConfig.map((question) => {
                  let field: React.ReactElement | null = null;
                  let errorMessage: string | undefined = '';
                  if (
                    showValidation &&
                    question.required &&
                    (!address[question.key] || address[question.key]?.trim() == '')
                  ) {
                    errorMessage = getErrorMessage({ isValid: false, type: 'required' });
                  }
                  if (question.type === 'single_line') {
                    field = (
                      <TextInput
                        title={question.label}
                        errorText={errorMessage}
                        inputMode="text"
                        id={question.key == 'postalCode' ? 'postal_code' : question.key}
                        name={question.key == 'postalCode' ? 'postal_code' : question.key}
                        invalid={!!errorMessage}
                        placeholder={question.placeholder}
                        value={address[question.key] ?? ''}
                        onChangeText={(text: string) => {
                          setAddress({ ...address, [question.key]: text });
                        }}
                        size={smallerThanMD ? 'medium' : 'large'}
                      />
                    );
                  } else if (question.type === 'combo_input') {
                    field = (
                      <Field<ComboInputElementProps>
                        InputComponent={ComboInputElement}
                        label={question.label}
                        errorText={errorMessage}
                        input={{
                          id: question.key,
                          name: question.key,
                          // @ts-expect-error - TODO add error to ComboInputElementProps in cinderblocks
                          error: !!errorMessage,
                          placeholder: question.placeholder,
                          onChange: (option: ComboInputOption) => {
                            setAddress({ ...address, [question.key]: option.value });
                          },
                          options: US_STATE_CODES_OPTIONS,
                          value: US_STATE_CODES_OPTIONS.find(
                            (option) => option.value === address[question.key],
                          ),
                          isClearable: false,
                        }}
                      />
                    );
                  }

                  return (
                    <QuestionFieldContainer key={question.key}>{field}</QuestionFieldContainer>
                  );
                })}
              </QuestionFieldsContainer>
            </form>
          </Column>
        </ColumnContainer>

        <BottomBar>
          <ProgressIndicator />
          <NavigationLayout
            nextButton={{
              buttonText: 'Next',
              loading: isModifyLoading,
              onClickHandler: nextButtonOnClickHandler,
            }}
            skipButton={{
              buttonText: 'Cancel',
              loading: loadingCancel,
              onClickHandler: () => {
                // Disable "Changes you made may not be saved." prompt
                window.removeEventListener('beforeunload', showPrompt);
                setLoadingCancel(true);
                const hadInitialAddress = !isAddressEmpty(initialAddress);
                dispatch(cancelManualAddressVerification(hadInitialAddress));
              },
            }}
          />
        </BottomBar>
      </TwoColumnPageContent>
    </PageLayout>
  );
};

const ManualAddressEntryWithSellerInput = () => (
  <ReceptionLoader>
    <ManualAddressEntryPage />
  </ReceptionLoader>
);

export default ManualAddressEntryWithSellerInput;
